/**
 * Per-rule remediation templates, keyed on the axe-core rule id.
 * Shared by the extension side panel and the web app issue view.
 *
 * Every entry is a curated, generic corrected pattern: nothing here is
 * generated by an LLM and nothing here reads page content. For rules not in
 * the map, callers fall back to the engine's own help text and help URL.
 *
 * Coverage is the point of the `coverage` export below: the pricing page and
 * the in-product upsell both describe this feature, and neither should promise
 * a before and after for a rule that has none. `tests/remediation.test.js`
 * fails if an entry is malformed, so the count stays honest as rules are added.
 */
export const REMEDIATION = {
  "button-name": {
    cause: "The button has no accessible name, so assistive technology announces it as just 'button' with no hint of what it does.",
    before: '<button>\n  <svg viewBox="0 0 24 24">...</svg>\n</button>',
    after: '<button aria-label="Search">\n  <svg viewBox="0 0 24 24" aria-hidden="true">...</svg>\n</button>',
  },
  "link-name": {
    cause: "The link has no accessible name, so screen reader users hear 'link' without knowing where it goes.",
    before: '<a href="/pricing">\n  <img src="chart.png" alt="">\n</a>',
    after: '<a href="/pricing">\n  <img src="chart.png" alt="View pricing">\n</a>',
  },
  "image-alt": {
    cause: "The image has no alt attribute. Screen readers either skip it or read the file name aloud.",
    before: '<img src="team-photo.jpg">',
    after: '<img src="team-photo.jpg" alt="The support team at the annual meetup">\n<!-- purely decorative images get an empty alt -->\n<img src="divider.svg" alt="">',
  },
  "input-image-alt": {
    cause: "The image button has no alt text, so its purpose is invisible to assistive technology.",
    before: '<input type="image" src="search-icon.png">',
    after: '<input type="image" src="search-icon.png" alt="Search">',
  },
  "role-img-alt": {
    cause: "The element is presented as an image (role=\"img\") but carries no accessible name.",
    before: '<div role="img" class="hero-illustration"></div>',
    after: '<div role="img" class="hero-illustration" aria-label="Auditor reviewing a report"></div>',
  },
  "svg-img-alt": {
    cause: "The SVG is exposed as an image but has no accessible name.",
    before: '<svg role="img" viewBox="0 0 24 24">...</svg>',
    after: '<svg role="img" viewBox="0 0 24 24" aria-label="Warning">\n  <title>Warning</title>\n  ...\n</svg>',
  },
  "area-alt": {
    cause: "The image map area has no alt text, so its target is unnamed for assistive technology.",
    before: '<area shape="rect" coords="0,0,80,40" href="/north">',
    after: '<area shape="rect" coords="0,0,80,40" href="/north" alt="Northern region">',
  },
  label: {
    cause: "The form field has no associated label. A placeholder is not a label: it disappears on input and is not reliably announced.",
    before: '<input type="email" placeholder="Email address">',
    after: '<label for="email">Email address</label>\n<input id="email" type="email" autocomplete="email">',
  },
  "select-name": {
    cause: "The select element has no accessible name, so users do not know what they are choosing.",
    before: '<select>\n  <option>EUR</option>\n  <option>USD</option>\n</select>',
    after: '<label for="currency">Currency</label>\n<select id="currency">\n  <option>EUR</option>\n  <option>USD</option>\n</select>',
  },
  "color-contrast": {
    cause: "The text does not reach the 4.5:1 contrast ratio against its background (3:1 for large text), so it is hard or impossible to read for many users.",
    before: ".hint {\n  color: #999999;\n  background: #ffffff; /* 2.8:1, fails */\n}",
    after: ".hint {\n  color: #595959;\n  background: #ffffff; /* 7.0:1, passes AA */\n}",
  },
  "html-has-lang": {
    cause: "The page does not declare its language, so screen readers may read it with the wrong pronunciation rules.",
    before: "<html>",
    after: '<html lang="en">',
  },
  "html-lang-valid": {
    cause: "The declared page language is not a valid BCP 47 language tag, so it is ignored by assistive technology.",
    before: '<html lang="english">',
    after: '<html lang="en">',
  },
  "document-title": {
    cause: "The page has no title, which is the first thing announced when the page loads and the label used in tabs and bookmarks.",
    before: "<head>\n  <meta charset=\"utf-8\">\n</head>",
    after: "<head>\n  <meta charset=\"utf-8\">\n  <title>Order history | Example Shop</title>\n</head>",
  },
  "td-headers-attr": {
    cause: "A data cell references header cells that do not exist or are not header cells, so the association is broken for screen readers.",
    before: '<td headers="missing-id">42</td>',
    after: '<th id="q1">Q1 revenue</th>\n...\n<td headers="q1">42</td>',
  },
  "th-has-data-cells": {
    cause: "A table header is not associated with any data cells, so screen readers cannot relate the data back to its header.",
    before: "<tr>\n  <th>Plan</th>\n  <th>Price</th>\n</tr>\n<tr>\n  <td colspan=\"2\">See below</td>\n</tr>",
    after: '<tr>\n  <th scope="col">Plan</th>\n  <th scope="col">Price</th>\n</tr>\n<tr>\n  <td>Solo</td>\n  <td>29 USD</td>\n</tr>',
  },
  list: {
    cause: "The list contains children that are not list items, which breaks how screen readers count and navigate the list.",
    before: "<ul>\n  <div>First point</div>\n  <div>Second point</div>\n</ul>",
    after: "<ul>\n  <li>First point</li>\n  <li>Second point</li>\n</ul>",
  },
  listitem: {
    cause: "A list item is not inside a list container, so it loses its list semantics entirely.",
    before: "<div>\n  <li>Orphaned item</li>\n</div>",
    after: "<ul>\n  <li>Orphaned item</li>\n</ul>",
  },
  "frame-title": {
    cause: "The iframe has no title, so users navigating by frame hear no description of what it contains.",
    before: '<iframe src="https://maps.example.com/embed"></iframe>',
    after: '<iframe src="https://maps.example.com/embed" title="Map showing the office location"></iframe>',
  },
  "heading-order": {
    cause: "Heading levels skip (for example h2 straight to h4), which breaks the outline that screen reader users navigate by.",
    before: "<h2>Billing</h2>\n<h4>Payment methods</h4>",
    after: "<h2>Billing</h2>\n<h3>Payment methods</h3>",
  },
  "meta-viewport": {
    cause: "The viewport meta tag blocks zooming, which prevents low-vision users from enlarging the page.",
    before: '<meta name="viewport" content="width=device-width, user-scalable=no, maximum-scale=1">',
    after: '<meta name="viewport" content="width=device-width, initial-scale=1">',
  },
  tabindex: {
    cause: "A positive tabindex forces a custom tab order, which almost always ends up illogical as the page evolves. Keyboard focus should follow the document order.",
    before: '<button tabindex="3">Save</button>',
    after: '<button tabindex="0">Save</button>\n<!-- or simply omit tabindex on natively focusable elements -->\n<button>Save</button>',
  },
  "aria-hidden-focus": {
    cause: "An element hidden from assistive technology (aria-hidden) still receives keyboard focus, so keyboard users land on something screen readers pretend does not exist.",
    before: '<div aria-hidden="true">\n  <button>Close</button>\n</div>',
    after: '<div aria-hidden="true">\n  <button tabindex="-1">Close</button>\n</div>\n<!-- or remove aria-hidden if the content should be reachable -->',
  },

  /* Accessible names on ARIA widgets. Custom components are where names go
     missing most often, because nothing native supplies one. */
  "aria-command-name": {
    cause: "The element exposes a command role (button, link, or menuitem) with no accessible name, so it is announced by role alone.",
    before: '<div role="button" onclick="save()">\n  <svg viewBox="0 0 24 24">...</svg>\n</div>',
    after: '<button type="button" onclick="save()" aria-label="Save draft">\n  <svg viewBox="0 0 24 24" aria-hidden="true">...</svg>\n</button>\n<!-- a native button is preferable to role="button" -->',
  },
  "aria-input-field-name": {
    cause: "The custom input field (combobox, listbox, searchbox, slider, spinbutton, or textbox role) has no accessible name, so its purpose is unknown.",
    before: '<div role="combobox" aria-expanded="false"></div>',
    after: '<label id="country-label">Country</label>\n<div role="combobox" aria-expanded="false" aria-labelledby="country-label"></div>',
  },
  "aria-toggle-field-name": {
    cause: "The custom checkbox, radio, or switch has no accessible name, so it announces only its checked state.",
    before: '<div role="switch" aria-checked="false"></div>',
    after: '<div role="switch" aria-checked="false" aria-label="Email notifications"></div>',
  },
  "aria-dialog-name": {
    cause: "The dialog has no accessible name, so screen reader users are moved into it without being told what it is.",
    before: '<div role="dialog" aria-modal="true">\n  <h2>Delete this site?</h2>\n</div>',
    after: '<div role="dialog" aria-modal="true" aria-labelledby="dlg-title">\n  <h2 id="dlg-title">Delete this site?</h2>\n</div>',
  },
  "aria-progressbar-name": {
    cause: "The progress bar has no accessible name, so its value is announced without saying what is progressing.",
    before: '<div role="progressbar" aria-valuenow="60" aria-valuemin="0" aria-valuemax="100"></div>',
    after: '<div role="progressbar" aria-label="Upload progress" aria-valuenow="60" aria-valuemin="0" aria-valuemax="100"></div>',
  },
  "aria-meter-name": {
    cause: "The meter has no accessible name, so the measurement it reports has no meaning to assistive technology.",
    before: '<div role="meter" aria-valuenow="72" aria-valuemin="0" aria-valuemax="100"></div>',
    after: '<div role="meter" aria-label="Storage used" aria-valuenow="72" aria-valuemin="0" aria-valuemax="100"></div>',
  },
  "aria-tooltip-name": {
    cause: "The tooltip has no accessible name, so it contributes nothing when referenced.",
    before: '<div role="tooltip" id="tip"></div>',
    after: '<div role="tooltip" id="tip">Opens in a new window</div>\n<a href="/docs" aria-describedby="tip">Documentation</a>',
  },

  /* ARIA validity. These fire when a role is used without the contract it
     carries, which usually leaves the widget unusable rather than merely
     mislabelled. */
  "aria-required-attr": {
    cause: "The role requires ARIA attributes that are missing, so the widget cannot report its own state.",
    before: '<div role="checkbox" tabindex="0">Send me updates</div>',
    after: '<div role="checkbox" tabindex="0" aria-checked="false">Send me updates</div>',
  },
  "aria-required-children": {
    cause: "The role requires specific child roles that are absent, so assistive technology cannot interpret the structure.",
    before: '<ul role="list">\n  <div>Solo</div>\n  <div>Agency</div>\n</ul>',
    after: '<ul role="list">\n  <li role="listitem">Solo</li>\n  <li role="listitem">Agency</li>\n</ul>',
  },
  "aria-required-parent": {
    cause: "The role must sit inside a specific parent role and does not, so it loses its meaning entirely.",
    before: '<div>\n  <div role="option">Monthly</div>\n</div>',
    after: '<div role="listbox" aria-label="Billing cycle">\n  <div role="option" aria-selected="true">Monthly</div>\n</div>',
  },
  "aria-allowed-attr": {
    cause: "An ARIA attribute is not allowed on this role, so it is either ignored or actively misleading.",
    before: '<div role="heading" aria-level="2" aria-checked="true">Billing</div>',
    after: '<div role="heading" aria-level="2">Billing</div>\n<!-- better still, use a native heading -->\n<h2>Billing</h2>',
  },
  "aria-allowed-role": {
    cause: "The role is not permitted on this element type, so browsers may drop it and expose the native semantics instead.",
    before: '<li role="button">Delete</li>',
    after: '<li>\n  <button type="button">Delete</button>\n</li>',
  },
  "aria-valid-attr": {
    cause: "The attribute is not a real ARIA attribute (usually a typo), so it does nothing at all.",
    before: '<button aria-labell="Close">x</button>',
    after: '<button aria-label="Close">x</button>',
  },
  "aria-valid-attr-value": {
    cause: "The ARIA attribute holds a value the specification does not allow, or points at an id that does not exist on the page.",
    before: '<input aria-labelledby="missing-id">\n<div aria-expanded="open"></div>',
    after: '<span id="email-label">Email</span>\n<input aria-labelledby="email-label">\n<div aria-expanded="true"></div>',
  },
  "aria-roles": {
    cause: "The role value is not a valid ARIA role, so the element falls back to whatever semantics it had (often none).",
    before: '<div role="tab-panel">...</div>',
    after: '<div role="tabpanel">...</div>',
  },
  "aria-hidden-body": {
    cause: "aria-hidden is set on the body element, which hides the entire page from assistive technology.",
    before: '<body aria-hidden="true">',
    after: "<body>\n<!-- when a modal is open, hide the background container instead of body -->",
  },

  /* Structure and navigation. */
  bypass: {
    cause: "The page offers no way to skip past repeated navigation, so keyboard and screen reader users tab through the whole header on every page.",
    before: "<body>\n  <nav>...40 links...</nav>\n  <div>Page content</div>\n</body>",
    after: '<body>\n  <a class="skip-link" href="#main">Skip to main content</a>\n  <nav>...</nav>\n  <main id="main" tabindex="-1">Page content</main>\n</body>',
  },
  region: {
    cause: "Content sits outside any landmark, so users who navigate by landmark cannot reach it directly.",
    before: "<div class=\"content\">\n  <h1>Reports</h1>\n</div>",
    after: '<main>\n  <h1>Reports</h1>\n</main>\n<!-- header, nav, main, aside and footer all create landmarks -->',
  },
  "landmark-one-main": {
    cause: "The page has no main landmark, or more than one, so the primary content region is ambiguous.",
    before: "<div id=\"app\">\n  <div class=\"page\">...</div>\n</div>",
    after: '<div id="app">\n  <main>...</main>\n</div>',
  },
  "duplicate-id-aria": {
    cause: "An id referenced by ARIA appears more than once, so the reference resolves to the wrong element or to nothing.",
    before: '<span id="hint">Required</span>\n<span id="hint">Optional</span>\n<input aria-describedby="hint">',
    after: '<span id="email-hint">Required</span>\n<span id="phone-hint">Optional</span>\n<input aria-describedby="email-hint">',
  },
  "empty-heading": {
    cause: "The heading has no text content, so it appears in the heading outline as an empty entry users cannot act on.",
    before: '<h2 class="icon-only"></h2>',
    after: "<h2>Open issues</h2>\n<!-- or remove the heading if it carries no content -->",
  },
  "empty-table-header": {
    cause: "A table header cell is empty, leaving the column or row it governs unnamed.",
    before: '<th scope="col"></th>\n<th scope="col">Price</th>',
    after: '<th scope="col">Plan</th>\n<th scope="col">Price</th>',
  },
  "nested-interactive": {
    cause: "An interactive control is nested inside another, which leaves an unpredictable focus order and a control screen readers cannot announce coherently.",
    before: "<button>\n  Delete <a href=\"/help\">why?</a>\n</button>",
    after: '<button type="button">Delete</button>\n<a href="/help">Why?</a>',
  },
  "scrollable-region-focusable": {
    cause: "A scrollable region cannot receive keyboard focus, so keyboard users cannot scroll it at all.",
    before: '<div style="overflow:auto; max-height:300px">\n  ...long content...\n</div>',
    after: '<div style="overflow:auto; max-height:300px" tabindex="0" role="region" aria-label="Issue log">\n  ...long content...\n</div>',
  },
  "presentation-role-conflict": {
    cause: "The element is marked as presentational but is still focusable or carries ARIA, so its semantics are removed while it stays interactive.",
    before: '<button role="none" aria-label="Close">x</button>',
    after: '<button aria-label="Close">x</button>\n<!-- role="none" belongs on non-interactive wrappers only -->',
  },

  /* Forms. */
  "form-field-multiple-labels": {
    cause: "The field has more than one label element, and browsers disagree on which one wins, so the announced name is unpredictable.",
    before: '<label for="dob">Date</label>\n<label for="dob">of birth</label>\n<input id="dob">',
    after: '<label for="dob">Date of birth</label>\n<input id="dob">',
  },
  "input-button-name": {
    cause: "The input button has no value or accessible name, so it is announced as an unnamed button.",
    before: '<input type="submit">\n<input type="button">',
    after: '<input type="submit" value="Create account">\n<input type="button" value="Cancel">',
  },
  "autocomplete-valid": {
    cause: "The autocomplete attribute holds a token the specification does not define, so browsers and assistive technology cannot infer the field's purpose.",
    before: '<input name="email" autocomplete="your-email">',
    after: '<input name="email" autocomplete="email">',
  },
  "label-title-only": {
    cause: "The field is named only by a title or aria-describedby, which is unreliable: title text is not shown on touch devices and is inconsistently announced.",
    before: '<input type="text" title="Postal code">',
    after: '<label for="postcode">Postal code</label>\n<input id="postcode" type="text" autocomplete="postal-code">',
  },
  "summary-name": {
    cause: "The disclosure summary has no accessible name, so the control that expands the section is announced without a purpose.",
    before: "<details>\n  <summary></summary>\n  <p>Refund policy text.</p>\n</details>",
    after: "<details>\n  <summary>Refund policy</summary>\n  <p>Refund policy text.</p>\n</details>",
  },

  /* Media and embedded content. */
  "object-alt": {
    cause: "The object element has no text alternative, so its content is unavailable to anyone who cannot perceive it.",
    before: '<object data="chart.svg" type="image/svg+xml"></object>',
    after: '<object data="chart.svg" type="image/svg+xml">\n  Revenue by quarter: Q1 40k, Q2 55k, Q3 61k, Q4 72k.\n</object>',
  },
  "video-caption": {
    cause: "The video has no captions track, so deaf and hard of hearing users get no access to the spoken content.",
    before: '<video src="demo.mp4" controls></video>',
    after: '<video src="demo.mp4" controls>\n  <track kind="captions" src="demo-en.vtt" srclang="en" label="English" default>\n</video>',
  },
  "audio-caption": {
    cause: "The audio has no captions track, so its content is unavailable to deaf and hard of hearing users.",
    before: '<audio src="interview.mp3" controls></audio>',
    after: '<audio src="interview.mp3" controls>\n  <track kind="captions" src="interview-en.vtt" srclang="en" label="English">\n</audio>\n<!-- publish a transcript alongside the player as well -->',
  },
  "no-autoplay-audio": {
    cause: "Audio plays automatically for more than three seconds with no way to stop it, which drowns out a screen reader.",
    before: '<audio src="intro.mp3" autoplay></audio>',
    after: '<audio src="intro.mp3" controls></audio>\n<!-- or keep autoplay muted, and give an explicit control to unmute -->',
  },
  "frame-focusable-content": {
    cause: "The iframe is hidden from assistive technology yet still contains focusable elements, so keyboard focus disappears into content screen readers cannot see.",
    before: '<iframe src="/widget" tabindex="-1" aria-hidden="true"></iframe>',
    after: '<iframe src="/widget" title="Support chat"></iframe>\n<!-- if the frame really is decorative, make its content unfocusable too -->',
  },
  "frame-title-unique": {
    cause: "Several iframes share the same title, so users navigating by frame cannot tell them apart.",
    before: '<iframe title="Video"></iframe>\n<iframe title="Video"></iframe>',
    after: '<iframe title="Product tour video"></iframe>\n<iframe title="Customer testimonial video"></iframe>',
  },
  "server-side-image-map": {
    cause: "Server side image maps require a mouse: there is no keyboard equivalent for clicking coordinates.",
    before: '<a href="/regions"><img src="map.png" ismap alt="Regions"></a>',
    after: '<img src="map.png" usemap="#regions" alt="Regions">\n<map name="regions">\n  <area shape="rect" coords="0,0,80,40" href="/north" alt="Northern region">\n</map>',
  },

  /* Text, language and motion. */
  "valid-lang": {
    cause: "A lang attribute inside the page holds an invalid language tag, so that passage is read with the wrong pronunciation rules.",
    before: '<p lang="fre">Bonjour</p>',
    after: '<p lang="fr">Bonjour</p>',
  },
  "html-xml-lang-mismatch": {
    cause: "The lang and xml:lang attributes on the root element disagree, so which language applies is undefined.",
    before: '<html lang="en" xml:lang="fr">',
    after: '<html lang="en" xml:lang="en">',
  },
  "meta-refresh": {
    cause: "The page refreshes or redirects on a timer, which can move users away before they have finished reading or filling in a form.",
    before: '<meta http-equiv="refresh" content="15">',
    after: "<!-- remove the meta refresh; if the content must update, offer a control -->\n<button type=\"button\" onclick=\"reload()\">Refresh results</button>",
  },
  "avoid-inline-spacing": {
    cause: "Letter, word, or line spacing is set inline with !important, so users who override text spacing to read comfortably cannot.",
    before: '<p style="line-height: 1.1 !important">Terms and conditions...</p>',
    after: "<p class=\"terms\">Terms and conditions...</p>\n<style>\n  .terms { line-height: 1.6; }\n</style>",
  },
  "link-in-text-block": {
    cause: "The link is distinguished from surrounding text by color alone, at less than 3:1 against it, so users who do not perceive that color difference cannot find it.",
    before: ".prose a {\n  color: #3B6FB0;\n  text-decoration: none;\n}",
    after: ".prose a {\n  color: #16324F;\n  text-decoration: underline;\n}",
  },
  blink: {
    cause: "The blink element flashes content indefinitely, which is distracting and can trigger seizures.",
    before: "<blink>Limited offer</blink>",
    after: '<strong class="offer">Limited offer</strong>',
  },
  marquee: {
    cause: "The marquee element scrolls content with no way to pause it, and moving text is unreadable for many users.",
    before: "<marquee>Latest updates</marquee>",
    after: '<div class="updates">Latest updates</div>\n<!-- if motion is required, give an explicit pause control and honour prefers-reduced-motion -->',
  },

  /* Tables and definition lists. */
  "definition-list": {
    cause: "The definition list contains children other than the dt, dd and div wrappers it allows, which breaks the term to definition pairing.",
    before: "<dl>\n  <p>Solo: one practitioner</p>\n</dl>",
    after: "<dl>\n  <dt>Solo</dt>\n  <dd>One practitioner</dd>\n</dl>",
  },
  dlitem: {
    cause: "A dt or dd sits outside a definition list, so it has no term to pair with.",
    before: "<div>\n  <dt>Solo</dt>\n  <dd>One practitioner</dd>\n</div>",
    after: "<dl>\n  <dt>Solo</dt>\n  <dd>One practitioner</dd>\n</dl>",
  },
  "scope-attr-valid": {
    cause: "The scope attribute holds a value other than row or col, so the header to data association is not established.",
    before: '<th scope="column">Price</th>',
    after: '<th scope="col">Price</th>',
  },
  "td-has-header": {
    cause: "Data cells in a large table have no associated header, so a screen reader reads values with nothing to anchor them to.",
    before: "<td>72</td>",
    after: '<th scope="row">Q4</th>\n<td>72</td>\n<!-- or associate explicitly with headers="..." on complex tables -->',
  },
  "table-fake-caption": {
    cause: "A row of cells is used as a visual caption instead of a caption element, so it is not announced as the table's name.",
    before: "<table>\n  <tr><td colspan=\"3\">Open issues by impact</td></tr>",
    after: "<table>\n  <caption>Open issues by impact</caption>",
  },
  "target-size": {
    cause: "The control is smaller than 24 by 24 CSS pixels and sits close to its neighbours, so it is hard to hit accurately (WCAG 2.2, SC 2.5.8).",
    before: ".icon-btn {\n  width: 16px;\n  height: 16px;\n}",
    after: ".icon-btn {\n  width: 24px;\n  height: 24px;\n  /* or keep the icon small and add padding to reach 24px of target */\n}",
  },

  /* The remaining ARIA rules in the WCAG A/AA set. Less frequent than the
     naming rules above, but included so every rule the engine can report at
     this conformance target has a pattern rather than a link. */
  "aria-braille-equivalent": {
    cause: "aria-braillelabel or aria-brailleroledescription is set without the plain text equivalent it is meant to refine, so braille users get a label sighted screen reader users never hear.",
    before: '<button aria-braillelabel="Sv">Save</button>\n<div aria-brailleroledescription="btn"></div>',
    after: '<button aria-label="Save" aria-braillelabel="Sv">Save</button>\n<!-- braille variants only refine an existing label or roledescription -->',
  },
  "aria-conditional-attr": {
    cause: "The ARIA attribute is only valid on this role under conditions the element does not meet, so browsers ignore it or expose a contradictory state.",
    before: '<ul role="list">\n  <li role="listitem" aria-checked="true">Weekly digest</li>\n</ul>',
    after: '<ul role="list">\n  <li>\n    <input type="checkbox" id="digest" checked>\n    <label for="digest">Weekly digest</label>\n  </li>\n</ul>',
  },
  "aria-deprecated-role": {
    cause: "The role has been removed from the ARIA specification, so support for it is inconsistent and shrinking.",
    before: '<div role="directory">\n  <a href="/a">Alpha</a>\n</div>',
    after: '<ul>\n  <li><a href="/a">Alpha</a></li>\n</ul>',
  },
  "aria-prohibited-attr": {
    cause: "The role forbids this attribute, most often a name on an element that has no role able to carry one, so the name is dropped or announced twice.",
    before: '<p aria-label="Introduction">Complaudax tracks issues over time.</p>',
    after: '<section aria-label="Introduction">\n  <p>Complaudax tracks issues over time.</p>\n</section>',
  },
  "aria-roledescription": {
    cause: "aria-roledescription is set on an element with no role, or with a role that does not support it, so the custom description is discarded.",
    before: '<div aria-roledescription="carousel">...</div>',
    after: '<div role="region" aria-roledescription="carousel" aria-label="Featured reports">...</div>',
  },
  "aria-tab-name": {
    cause: "The tab has no accessible name, so users moving through a tab list hear position and state but never the destination.",
    before: '<div role="tablist">\n  <div role="tab" aria-selected="true"></div>\n</div>',
    after: '<div role="tablist" aria-label="Site sections">\n  <button role="tab" aria-selected="true" aria-controls="issues-panel">Issues</button>\n</div>',
  },
  "css-orientation-lock": {
    cause: "A CSS orientation media query locks the layout to one orientation, which excludes anyone whose device is mounted in the other (SC 1.3.4).",
    before: "@media screen and (orientation: landscape) {\n  body { transform: rotate(-90deg); }\n}",
    after: "/* let the layout reflow in both orientations */\n@media screen and (max-width: 640px) {\n  .grid { grid-template-columns: 1fr; }\n}",
  },
  "label-content-name-mismatch": {
    cause: "The accessible name does not contain the visible label text, so a speech input user saying what they see cannot activate the control (SC 2.5.3).",
    before: '<button aria-label="Submit form">Send message</button>',
    after: '<button aria-label="Send message to support">Send message</button>\n<!-- the accessible name must start with, or contain, the visible text -->',
  },
  "p-as-heading": {
    cause: "A paragraph is styled to look like a heading, so it reads as a heading visually but is absent from the outline screen reader users navigate by.",
    before: '<p style="font-size:22px;font-weight:bold">Billing history</p>',
    after: "<h2>Billing history</h2>\n<style>\n  h2 { font-size: 22px; }\n</style>",
  },
};

/** Number of rules carrying a curated before and after pattern. */
export const REMEDIATION_RULE_COUNT = Object.keys(REMEDIATION).length;

/**
 * Derive the fix guidance for one finding. Uses only data the engine already
 * returned (help text, help URL, failure summary, redacted snippet) plus the
 * curated template above. Never raw page content, never an LLM.
 *
 * `mapped: false` is a real answer, not a gap: axe's own failure summary names
 * the element and the condition that failed, and the help URL is the canonical
 * guidance for that rule. Callers render it as guidance rather than as a
 * missing feature.
 */
export function getRemediation(finding) {
  const entry = REMEDIATION[finding.rule_id];
  if (entry) {
    return { mapped: true, cause: entry.cause, before: entry.before, after: entry.after };
  }
  return {
    mapped: false,
    cause: finding.failure_summary || finding.description || "",
    before: null,
    after: null,
  };
}
